import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import { api } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import { 
  Buildings, Plus, ArrowRight, ArrowLeft, Check, Eye, PaperPlaneTilt,
  CheckCircle, Clock, Warning, FileText, MapPin, CurrencyEur, Percent,
  Calendar, TrendUp, CaretRight, PencilSimple, Trash, X, FloppyDisk,
  Star, Target, ChartLine, Timer, Image, Tag, Link as LinkIcon,
  Users, Bank, House, Briefcase, Info, ListBullets, ArrowsClockwise,
  SquaresFour, Table as TableIcon, UploadSimple, DownloadSimple, FileCsv
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { AdminPageHeader, AdminKpi, AdminKpiStrip } from '../../components/admin/AdminPageHeader';
import AdminToolbar from '../../components/admin/AdminToolbar';
import { PROJECT_TYPES, projectTypeLabel, normalizeProjectType } from '../../lib/projectTypes';

export const FUNDING_RANGES = [
  { value: '0-0', label: 'Not started' },
  { value: '1-49', label: 'Under 50%' },
  { value: '50-99', label: '50-99%' },
  { value: '100-1000', label: 'Fully funded' },
];

export const IRR_RANGES = [
  { value: '0-9.99', label: 'Under 10%' },
  { value: '10-14.99', label: '10-15%' },
  { value: '15-19.99', label: '15-20%' },
  { value: '20-1000', label: '20%+' },
];

export const HOLD_RANGES = [
  { value: '0-12', label: 'Up to 12 months' },
  { value: '13-24', label: '13-24 months' },
  { value: '25-36', label: '25-36 months' },
  { value: '37-1000', label: '36+ months' },
];

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: PencilSimple },
  pending_review: { label: 'In Review', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: Check },
  published: { label: 'Published', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  funding: { label: 'Funding', color: 'bg-emerald-100 text-emerald-700', icon: TrendUp },
  funded: { label: 'Funded', color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
  in_execution: { label: 'In Execution', color: 'bg-indigo-100 text-indigo-700', icon: Buildings },
  operating: { label: 'Operating', color: 'bg-teal-100 text-teal-700', icon: ArrowsClockwise },
  exiting: { label: 'Exiting', color: 'bg-orange-100 text-orange-700', icon: Timer },
  exited: { label: 'Exited', color: 'bg-slate-100 text-slate-700', icon: Check },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: Warning },
  archived: { label: 'Archived', color: 'bg-gray-100 text-gray-500', icon: FileText },
};

// Strategy and project type are one list - see lib/projectTypes.js
const projectTypeConfig = PROJECT_TYPES.reduce((acc, t) => {
  acc[t.value] = { label: t.label, description: t.description };
  return acc;
}, {});

const riskLevelConfig = {
  low: { label: 'Low', color: 'bg-green-100 text-green-700' },
  moderate: { label: 'Moderate', color: 'bg-yellow-100 text-yellow-700' },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
  high: { label: 'High', color: 'bg-red-100 text-red-700' },
};

/**
 * Project Card - Mobile optimized
 * Uses instrument-derived financial data when available
 */
const ProjectCard = ({ project, onClick }) => {
  const status = statusConfig[project.status] || statusConfig.draft;
  const StatusIcon = status.icon;
  
  // Use instrument-derived values when available
  const hasInstruments = project.has_instruments;
  const fundingTarget = parseFloat(project.funding_target || 0);
  const fundingCurrent = parseFloat(project.funding_current || 0);
  const fundingProgress = fundingTarget > 0 
    ? Math.min(100, (fundingCurrent / fundingTarget) * 100)
    : 0;
  
  // Return estimates from instrument
  const estimatedAnnualReturn = project.estimated_annual_return;
  const monthlyNoi = project.estimated_monthly_noi_pct;
  const totalAppreciation = project.estimated_total_appreciation_pct;
  const holdPeriod = project.target_hold_months || project.max_hold_period_months || project.duration_months;
  const minInvestment = project.min_investment || 100;

  return (
    <Card 
      className="bg-white rounded-lg sm:rounded-xl border border-black/[0.04] p-3 sm:p-5 cursor-pointer hover:border-primary/20 hover:shadow-md transition-all"
      onClick={() => onClick(project)}
      data-testid={`project-card-${project.code}`}
    >
      <div className="flex items-start justify-between mb-2 sm:mb-3 gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1 flex-wrap">
            <span className="font-mono text-[10px] sm:text-xs text-foreground-muted">{project.code}</span>
            <Badge className={cn('text-[10px] sm:text-xs px-1.5', status.color)}>
              <StatusIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
              {status.label}
            </Badge>
            {project.featured && (
              <Badge className="bg-amber-100 text-amber-700 text-[10px] sm:text-xs px-1.5 hidden sm:flex">
                <Star className="h-2.5 w-2.5 mr-0.5" weight="fill" />
                Featured
              </Badge>
            )}
            {!hasInstruments && (
              <Badge className="bg-gray-100 text-gray-500 text-[10px] sm:text-xs px-1.5">
                No Instrument
              </Badge>
            )}
          </div>
          <h3 className="font-semibold text-sm sm:text-base text-foreground truncate">{project.title}</h3>
          {project.tagline && <p className="text-xs sm:text-sm text-foreground-muted line-clamp-1">{project.tagline}</p>}
        </div>
      </div>
      
      <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-foreground-muted mb-2 sm:mb-3">
        <Badge variant="outline" className="text-[10px] sm:text-xs">
          {projectTypeLabel(project.project_type)}
        </Badge>
        {project.primary_location && (
          <span className="flex items-center gap-1 truncate">
            <MapPin className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{project.primary_location.city}</span>
          </span>
        )}
      </div>
      
      {/* Funding Progress - Simplified on mobile */}
      {hasInstruments ? (
        <div className="mb-2 sm:mb-3">
          <div className="flex items-center justify-between text-xs sm:text-sm mb-1">
            <span className="text-foreground-muted">Subscribed</span>
            <span className="font-medium">{fundingProgress.toFixed(0)}%</span>
          </div>
          <Progress value={fundingProgress} className="h-1.5 sm:h-2" />
          <div className="flex justify-between text-[10px] sm:text-xs text-foreground-muted mt-0.5 sm:mt-1">
            <span>€{parseInt(fundingCurrent).toLocaleString()}</span>
            <span className="hidden sm:inline">€{parseInt(fundingTarget).toLocaleString()} target</span>
          </div>
        </div>
      ) : (
        <div className="mb-2 sm:mb-3 p-2 bg-slate-100 rounded-lg text-center">
          <p className="text-xs text-foreground-muted">Investment terms pending - link an instrument</p>
        </div>
      )}
      
      {/* Key Metrics - From instrument when available */}
      <div className="grid grid-cols-3 gap-1 sm:gap-2 text-xs sm:text-sm mb-2 sm:mb-3 p-1.5 sm:p-2 bg-slate-50 rounded-lg">
        <div className="text-center">
          {hasInstruments && estimatedAnnualReturn ? (
            <>
              <p className="font-bold text-primary text-sm sm:text-base">{parseFloat(estimatedAnnualReturn).toFixed(1)}%</p>
              <p className="text-[10px] sm:text-xs text-foreground-muted">IRR (ann.)</p>
              {(monthlyNoi || totalAppreciation) && (
                <p className="text-[8px] sm:text-[10px] text-foreground-muted">
                  {monthlyNoi ? `NOI ${parseFloat(monthlyNoi).toFixed(2)}%/mo` : ''}
                  {monthlyNoi && totalAppreciation ? ' + ' : ''}
                  {totalAppreciation ? `Appr ${parseFloat(totalAppreciation).toFixed(1)}%` : ''}
                </p>
              )}
            </>
          ) : (
            <>
              <p className="font-bold text-foreground-muted text-sm sm:text-base">—</p>
              <p className="text-[10px] sm:text-xs text-foreground-muted">IRR (ann.)</p>
            </>
          )}
        </div>
        <div className="text-center border-x border-black/[0.04]">
          <p className="font-bold text-sm sm:text-base">{holdPeriod || '—'}m</p>
          <p className="text-[10px] sm:text-xs text-foreground-muted">Hold Period</p>
        </div>
        <div className="text-center">
          <p className="font-bold text-sm sm:text-base">€{parseInt(minInvestment).toLocaleString()}</p>
          <p className="text-[10px] sm:text-xs text-foreground-muted">Min</p>
        </div>
      </div>
      
      <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-black/[0.04] gap-2">
        <div className="min-w-0">
          <p className="text-[10px] sm:text-xs text-foreground-muted truncate" data-testid={`project-card-nav-${project.code}`}>
            {project.current_nav
              ? <>Asset NAV <span className="font-medium text-foreground">€{parseInt(project.current_nav).toLocaleString()}</span></>
              : 'No NAV recorded'}
          </p>
          <p className="text-[10px] sm:text-xs text-foreground-muted truncate">
            {hasInstruments && <span className="text-green-600 mr-1">●</span>}
            v{project.version} • {new Date(project.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-1 text-xs sm:text-sm text-primary flex-shrink-0">
          <span className="hidden sm:inline">View Details</span>
          <CaretRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </div>
      </div>
    </Card>
  );
};

/**
 * Create/Edit Project Form Dialog - with party dropdowns
 */
const ProjectFormDialog = ({ open, onOpenChange, project, onSave }) => {
  const isEdit = !!project?.id;
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  
  // Party data for dropdowns
  const [developers, setDevelopers] = useState([]);
  const [issuers, setIssuers] = useState([]);
  const [loadingParties, setLoadingParties] = useState(false);
  const [showCreateParty, setShowCreateParty] = useState(false);
  const [createPartyType, setCreatePartyType] = useState(null); // 'developer' or 'issuer'
  const [newPartyName, setNewPartyName] = useState('');
  
  const [formData, setFormData] = useState({
    // Basic
    title: '',
    tagline: '',
    slug: '',
    description: '',
    investment_thesis: '',
    card_thesis: '',
    project_type: 'renovate_sell',
    // Party relationships
    developer_id: '',
    issuer_id: '',
    // Location
    location_city: '',
    location_country_code: '',
    location_address: '',
    location_neighborhood: '',
    // Timeline (financial terms live on the instrument)
    duration_months: '12',
    // Dates
    raise_start_date: '',
    raise_end_date: '',
    operations_start_date: '',
    expected_exit_date: '',
    // Risk
    risk_level: 'medium',
    risk_factors: '',
    risk_mitigants: '',
    // Display
    featured: false,
    tags: '',
    investment_product_id: '',
    // Edit reason
    reason: '',
  });

  // Fetch parties for dropdowns
  useEffect(() => {
    if (open) {
      fetchParties();
    }
  }, [open]);

  const fetchParties = async () => {
    setLoadingParties(true);
    try {
      // Fetch developers
      const devRes = await api.get('/admin/v2/parties?party_type=developer&limit=200');
      setDevelopers(devRes.data.parties || []);
      
      // Fetch issuers
      const issRes = await api.get('/admin/v2/parties?party_type=issuer&limit=200');
      setIssuers(issRes.data.parties || []);
    } catch (err) {
      console.error('Failed to load parties', err);
    } finally {
      setLoadingParties(false);
    }
  };

  const handleCreateParty = async () => {
    if (!newPartyName.trim()) {
      toast.error('Party name is required');
      return;
    }
    
    try {
      const res = await api.post('/admin/v2/parties', {
        display_name: newPartyName,
        legal_name: newPartyName,
        party_type: createPartyType,
      });
      
      toast.success(`${createPartyType === 'developer' ? 'Developer' : 'Issuer'} created`);
      
      // Refresh parties and select the new one
      await fetchParties();
      
      if (createPartyType === 'developer') {
        setFormData({ ...formData, developer_id: res.data.party.id });
      } else {
        setFormData({ ...formData, issuer_id: res.data.party.id });
      }
      
      setShowCreateParty(false);
      setNewPartyName('');
      setCreatePartyType(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create party');
    }
  };

  useEffect(() => {
    if (project) {
      setFormData({
        title: project.title || '',
        tagline: project.tagline || '',
        slug: project.slug || '',
        description: project.description || '',
        investment_thesis: project.investment_thesis || '',
        card_thesis: project.card_thesis || '',
        project_type: normalizeProjectType(project.project_type),
        developer_id: project.developer_id || '',
        issuer_id: project.issuer_id || '',
        location_city: project.primary_location?.city || '',
        location_country_code: project.primary_location?.country_code || '',
        location_address: project.primary_location?.address_line_1 || '',
        location_neighborhood: project.primary_location?.neighborhood || '',
        duration_months: project.duration_months?.toString() || '12',
        raise_start_date: project.raise_start_date?.split('T')[0] || '',
        raise_end_date: project.raise_end_date?.split('T')[0] || '',
        operations_start_date: project.operations_start_date?.split('T')[0] || '',
        expected_exit_date: project.expected_exit_date?.split('T')[0] || '',
        risk_level: project.risk_level || 'medium',
        risk_factors: project.risk_factors || '',
        risk_mitigants: project.risk_mitigants || '',
        featured: project.featured || false,
        tags: project.tags?.join(', ') || '',
        investment_product_id: project.investment_product_id || '',
        reason: '',
      });
    } else {
      setFormData({
        title: '', tagline: '', slug: '', description: '', investment_thesis: '', card_thesis: '',
        project_type: 'renovate_sell', developer_id: '', issuer_id: '',
        location_city: '', location_country_code: '', location_address: '', location_neighborhood: '',
        duration_months: '12', raise_start_date: '', raise_end_date: '',
        operations_start_date: '', expected_exit_date: '', risk_level: 'medium',
        risk_factors: '', risk_mitigants: '', featured: false, tags: '',
        investment_product_id: '', reason: '',
      });
    }
    setActiveTab('basic');
  }, [project, open]);

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Project title is required');
      return;
    }
    if (isEdit && !formData.reason.trim()) {
      toast.error('Reason for change is required');
      return;
    }

    setLoading(true);
    try {
      // Build complete payload with all fields
      const payload = {
        title: formData.title,
        tagline: formData.tagline || null,
        description: formData.description || null,
        investment_thesis: formData.investment_thesis || null,
        card_thesis: formData.card_thesis || null,
        project_type: formData.project_type,
        
        // Party relationships
        developer_id: formData.developer_id || null,
        issuer_id: formData.issuer_id || null,
        
        // Location
        location_city: formData.location_city || null,
        location_country_code: formData.location_country_code || null,
        location_address: formData.location_address || null,
        location_neighborhood: formData.location_neighborhood || null,
        
        // Timeline (all financial terms belong to the instrument)
        duration_months: formData.duration_months ? parseInt(formData.duration_months) : null,
        
        // Dates
        raise_start_date: formData.raise_start_date || null,
        raise_end_date: formData.raise_end_date || null,
        operations_start_date: formData.operations_start_date || null,
        expected_exit_date: formData.expected_exit_date || null,
        
        // Risk
        risk_level: formData.risk_level || null,
        risk_factors: formData.risk_factors || null,
        risk_mitigants: formData.risk_mitigants || null,
        
        // Display
        featured: formData.featured || false,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : null,
        investment_product_id: formData.investment_product_id || null,
      };

      if (isEdit) {
        payload.reason = formData.reason;
        await api.put(`/admin/v2/projects/${project.id}`, payload);
        toast.success('Project updated successfully');
      } else {
        await api.post('/admin/v2/projects', payload);
        toast.success('Project created successfully');
      }
      
      onSave();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Project' : 'Create New Project'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update project details. All changes are audited.' : 'Create a new investment project.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basic">Basic</TabsTrigger>
            <TabsTrigger value="location">Location</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
            <TabsTrigger value="risk">Risk</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Project title"
                  data-testid="project-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Project Type</Label>
                <Select value={formData.project_type} onValueChange={(v) => setFormData({...formData, project_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(projectTypeConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tagline</Label>
              <Input
                value={formData.tagline}
                onChange={(e) => setFormData({...formData, tagline: e.target.value})}
                placeholder="Brief compelling description"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Full project description..."
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label>Card headline</Label>
              <Textarea
                value={formData.card_thesis}
                onChange={(e) => setFormData({...formData, card_thesis: e.target.value})}
                placeholder="One line shown on the public project card, e.g. Bought 18% under comparable sales"
                rows={2}
                maxLength={160}
                data-testid="card-thesis-input"
              />
              <p className="text-xs text-foreground-muted">
                Appears under the location on the card. Falls back to the tagline when empty.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Investment Thesis</Label>
              <Textarea
                value={formData.investment_thesis}
                onChange={(e) => setFormData({...formData, investment_thesis: e.target.value})}
                placeholder="Why this is a good investment..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Developer/Sponsor</Label>
                <div className="flex gap-2">
                  <Select 
                    value={formData.developer_id || '_none_'} 
                    onValueChange={(v) => {
                      if (v === '__create_new__') {
                        setCreatePartyType('developer');
                        setShowCreateParty(true);
                      } else {
                        setFormData({...formData, developer_id: v === '_none_' ? '' : v});
                      }
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder={loadingParties ? "Loading..." : "Select developer"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">— None —</SelectItem>
                      {developers.map(party => (
                        <SelectItem key={party.id} value={party.id}>
                          {party.display_name} ({party.code})
                        </SelectItem>
                      ))}
                      <Separator className="my-1" />
                      <SelectItem value="__create_new__" className="text-primary font-medium">
                        <Plus className="h-4 w-4 inline mr-1" /> Create New Developer
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Issuer</Label>
                <div className="flex gap-2">
                  <Select 
                    value={formData.issuer_id || '_none_'} 
                    onValueChange={(v) => {
                      if (v === '__create_new__') {
                        setCreatePartyType('issuer');
                        setShowCreateParty(true);
                      } else {
                        setFormData({...formData, issuer_id: v === '_none_' ? '' : v});
                      }
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder={loadingParties ? "Loading..." : "Select issuer"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">— None —</SelectItem>
                      {issuers.map(party => (
                        <SelectItem key={party.id} value={party.id}>
                          {party.display_name} ({party.code})
                        </SelectItem>
                      ))}
                      <Separator className="my-1" />
                      <SelectItem value="__create_new__" className="text-primary font-medium">
                        <Plus className="h-4 w-4 inline mr-1" /> Create New Issuer
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Investment Product ID</Label>
                <Input
                  value={formData.investment_product_id}
                  onChange={(e) => setFormData({...formData, investment_product_id: e.target.value})}
                  placeholder="External product identifier"
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch
                  checked={formData.featured}
                  onCheckedChange={(v) => setFormData({...formData, featured: v})}
                />
                <Label>Featured Project</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tags (comma separated)</Label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                placeholder="lisbon, residential, renovation"
              />
            </div>
          </TabsContent>

          <TabsContent value="location" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City *</Label>
                <Input
                  value={formData.location_city}
                  onChange={(e) => setFormData({...formData, location_city: e.target.value})}
                  placeholder="Lisbon"
                />
              </div>
              <div className="space-y-2">
                <Label>Country Code *</Label>
                <Input
                  value={formData.location_country_code}
                  onChange={(e) => setFormData({...formData, location_country_code: e.target.value})}
                  placeholder="PT"
                  maxLength={2}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Street Address</Label>
              <Input
                value={formData.location_address}
                onChange={(e) => setFormData({...formData, location_address: e.target.value})}
                placeholder="Full street address"
              />
            </div>

            <div className="space-y-2">
              <Label>Neighborhood</Label>
              <Input
                value={formData.location_neighborhood}
                onChange={(e) => setFormData({...formData, location_neighborhood: e.target.value})}
                placeholder="Alfama, Baixa, etc."
              />
            </div>
          </TabsContent>

          <TabsContent value="timeline" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Duration (months)</Label>
              <Input
                type="number"
                value={formData.duration_months}
                onChange={(e) => setFormData({...formData, duration_months: e.target.value})}
                placeholder="12"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fundraising Start Date</Label>
                <Input
                  type="date"
                  value={formData.raise_start_date}
                  onChange={(e) => setFormData({...formData, raise_start_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Fundraising End Date</Label>
                <Input
                  type="date"
                  value={formData.raise_end_date}
                  onChange={(e) => setFormData({...formData, raise_end_date: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Operations Start Date</Label>
                <Input
                  type="date"
                  value={formData.operations_start_date}
                  onChange={(e) => setFormData({...formData, operations_start_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Expected Exit Date</Label>
                <Input
                  type="date"
                  value={formData.expected_exit_date}
                  onChange={(e) => setFormData({...formData, expected_exit_date: e.target.value})}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="risk" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Risk Level</Label>
              <Select value={formData.risk_level} onValueChange={(v) => setFormData({...formData, risk_level: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(riskLevelConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Risk Factors</Label>
              <Textarea
                value={formData.risk_factors}
                onChange={(e) => setFormData({...formData, risk_factors: e.target.value})}
                placeholder="Key risk factors for this investment..."
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label>Risk Mitigants</Label>
              <Textarea
                value={formData.risk_mitigants}
                onChange={(e) => setFormData({...formData, risk_mitigants: e.target.value})}
                placeholder="How risks are being mitigated..."
                rows={4}
              />
            </div>
          </TabsContent>
        </Tabs>

        {isEdit && (
          <div className="mt-4 p-3 bg-amber-50 rounded-lg">
            <Label className="text-amber-700">Reason for Change *</Label>
            <Input
              value={formData.reason}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
              placeholder="Describe why this change is being made (min 5 chars)"
              className="mt-2"
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Project'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Create New Party Dialog */}
      <Dialog open={showCreateParty} onOpenChange={setShowCreateParty}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>
              Create New {createPartyType === 'developer' ? 'Developer' : 'Issuer'}
            </DialogTitle>
            <DialogDescription>
              Create a new {createPartyType} party to associate with this project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Party Name *</Label>
              <Input
                value={newPartyName}
                onChange={(e) => setNewPartyName(e.target.value)}
                placeholder={createPartyType === 'developer' ? "Developer Company Name" : "Issuer Name"}
                data-testid="new-party-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateParty(false);
              setNewPartyName('');
              setCreatePartyType(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleCreateParty} data-testid="create-party-submit">
              Create {createPartyType === 'developer' ? 'Developer' : 'Issuer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

/**
 * Bulk Import Dialog Component
 */
const BulkImportDialog = ({ open, onOpenChange, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = React.useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setResults(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/admin/v2/bulk/projects/template', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'projects_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a CSV file first');
      return;
    }

    setImporting(true);
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/admin/v2/bulk/projects/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResults(response.data);

      if (response.data.successful > 0) {
        toast.success(`Successfully imported ${response.data.successful} projects`);
        onSuccess?.();
      }
      if (response.data.failed > 0) {
        toast.error(`${response.data.failed} rows failed to import`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResults(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCsv className="h-5 w-5" />
            Bulk Import Projects
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file to create multiple projects at once. Download the template for the correct format.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Template Download */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-blue-800 font-medium">Download CSV Template</p>
                <p className="text-xs text-blue-600 mt-1">
                  Get a template with all supported fields and example data.
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={handleDownloadTemplate}
                  data-testid="download-template-btn"
                >
                  <DownloadSimple className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>Select CSV File</Label>
            <div 
              className={cn(
                "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                file ? "border-primary bg-primary/5" : "border-gray-200 hover:border-gray-300"
              )}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                data-testid="csv-file-input"
              />
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <FileCsv className="h-8 w-8 text-primary" />
                  <div className="text-left">
                    <p className="font-medium text-sm">{file.name}</p>
                    <p className="text-xs text-foreground-muted">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setResults(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <UploadSimple className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-foreground-muted">
                    Click to select or drag and drop a CSV file
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Import Results */}
          {results && (
            <div className="space-y-3">
              <Separator />
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="p-2 bg-gray-50 rounded">
                  <p className="text-lg font-bold">{results.total_rows}</p>
                  <p className="text-xs text-foreground-muted">Total Rows</p>
                </div>
                <div className="p-2 bg-green-50 rounded">
                  <p className="text-lg font-bold text-green-600">{results.successful}</p>
                  <p className="text-xs text-green-600">Successful</p>
                </div>
                <div className="p-2 bg-red-50 rounded">
                  <p className="text-lg font-bold text-red-600">{results.failed}</p>
                  <p className="text-xs text-red-600">Failed</p>
                </div>
                <div className="p-2 bg-gray-50 rounded">
                  <p className="text-lg font-bold text-gray-500">{results.skipped}</p>
                  <p className="text-xs text-gray-500">Skipped</p>
                </div>
              </div>

              {/* Detailed Results */}
              {results.results?.length > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left py-2 px-3">Row</th>
                        <th className="text-left py-2 px-3">Status</th>
                        <th className="text-left py-2 px-3">Title/Code</th>
                        <th className="text-left py-2 px-3">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.results.map((r, i) => (
                        <tr key={i} className="border-t">
                          <td className="py-2 px-3">{r.row_number}</td>
                          <td className="py-2 px-3">
                            <Badge className={cn(
                              'text-[10px]',
                              r.status === 'success' ? 'bg-green-100 text-green-700' :
                              r.status === 'error' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-600'
                            )}>
                              {r.status}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 font-mono">
                            {r.project_code || r.project_title || '—'}
                          </td>
                          <td className="py-2 px-3 text-foreground-muted max-w-[200px] truncate">
                            {r.error_message || (r.status === 'success' ? 'Created' : '')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {results ? 'Close' : 'Cancel'}
          </Button>
          {!results && (
            <Button 
              onClick={handleImport} 
              disabled={!file || importing}
              data-testid="import-projects-btn"
            >
              {importing ? (
                <>
                  <ArrowsClockwise className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <UploadSimple className="h-4 w-4 mr-2" />
                  Import Projects
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Project List Row Component
 */
const ProjectListRow = ({ project, onClick }) => {
  const status = statusConfig[project.status] || statusConfig.draft;
  const StatusIcon = status.icon;
  const fundingProgress = project.funding_target > 0 
    ? Math.min(100, (parseFloat(project.funding_current) / parseFloat(project.funding_target)) * 100)
    : 0;

  return (
    <tr 
      className="border-b border-black/[0.04] hover:bg-slate-50 cursor-pointer transition-colors"
      onClick={() => onClick(project)}
      data-testid={`project-row-${project.code}`}
    >
      <td className="py-3 px-4">
        <span className="font-mono text-[10px] text-foreground-muted block">{project.code}</span>
        <span className="text-sm font-medium">{project.title}</span>
      </td>
      <td className="py-3 px-4 text-sm">
        <Badge className={cn('text-xs', status.color)}>
          <StatusIcon className="h-3 w-3 mr-1" />
          {status.label}
        </Badge>
      </td>
      <td className="py-3 px-4 text-sm">
        <Badge variant="outline" className="text-xs">
          {projectTypeLabel(project.project_type)}
        </Badge>
      </td>
      <td className="py-3 px-4 text-sm">
        {project.primary_location ? (
          <span className="text-sm">{project.primary_location.city}, {project.primary_location.country_code}</span>
        ) : (
          <span className="text-foreground-muted text-sm">—</span>
        )}
      </td>
      <td className="py-3 px-4 text-sm">
        <div className="w-32">
          <div className="flex justify-between text-xs mb-1">
            <span>€{parseInt(project.funding_current).toLocaleString()}</span>
            <span className="text-foreground-muted">{fundingProgress.toFixed(0)}%</span>
          </div>
          <Progress value={fundingProgress} className="h-1.5" />
        </div>
      </td>
      <td className="py-3 px-4 text-center text-sm">
        <span className="font-medium text-primary">
          {project.estimated_annual_return ? `${parseFloat(project.estimated_annual_return).toFixed(1)}%` : '—'}
        </span>
      </td>
      <td className="py-3 px-4 text-center text-sm">
        <span>{project.target_hold_months || project.duration_months || '—'}m</span>
      </td>
      <td className="py-3 px-4 text-right text-sm">
        <span className="text-xs text-foreground-muted">
          {new Date(project.created_at).toLocaleDateString()}
        </span>
      </td>
    </tr>
  );
};

/**
 * Main Projects Dashboard Page
 */
export default function AdminProjectsDashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || 'all',
    project_type: 'all',
    funding: 'all',
    irr: 'all',
    hold: 'all',
  });
  const filterStatus = filters.status;
  const [viewMode, setViewMode] = useState(searchParams.get('view') || 'card'); // 'card' or 'list'
  
  const [createOpen, setCreateOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('include_drafts', 'true');
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const [projectsRes, statsRes] = await Promise.all([
        // Use /admin/projects which includes instrument-derived financial data
        api.get(`/admin/projects?${params}`),
        api.get('/admin/projects/stats')
      ]);
      
      setProjects(projectsRes.data.projects || projectsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const typeOptions = useMemo(() => {
    const seen = new Map();
    projects.forEach((p) => {
      if (p.project_type) seen.set(normalizeProjectType(p.project_type), projectTypeLabel(p.project_type));
    });
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [projects]);

  const visibleProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    const inRange = (value, range) => {
      if (range === 'all') return true;
      const [min, max] = range.split('-').map(Number);
      const v = Number(value || 0);
      return v >= min && (Number.isNaN(max) || v <= max);
    };

    let list = projects.filter((p) => {
      if (q) {
        const haystack = [p.title, p.code, p.city, p.country_code, p.address_line_1, p.project_type_label]
          .filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (filters.project_type !== 'all' && normalizeProjectType(p.project_type) !== filters.project_type) return false;
      if (!inRange(p.funding_progress, filters.funding)) return false;
      if (!inRange(p.estimated_annual_return, filters.irr)) return false;
      if (!inRange(p.target_hold_months, filters.hold)) return false;
      return true;
    });

    const num = (v) => Number(v || 0);
    if (sort === 'irr') list = [...list].sort((a, b) => num(b.estimated_annual_return) - num(a.estimated_annual_return));
    else if (sort === 'funding') list = [...list].sort((a, b) => num(b.funding_progress) - num(a.funding_progress));
    else if (sort === 'title') list = [...list].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    return list;
  }, [projects, search, filters, sort]);

  // Navigate to full-screen project detail page
  const handleProjectClick = (project) => {
    navigate(`/admin/v2/projects/${project.id}`);
  };
  
  // Toggle view mode and update URL
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('view', mode);
    setSearchParams(newParams);
  };

  return (
    <div className="max-w-[1200px] space-y-6" data-testid="admin-projects-dashboard-page">
      <AdminPageHeader
        eyebrow="Portfolio"
        title="Projects"
        subtitle="Every investment project, its workflow stage and funding progress."
        actions={(
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkImportOpen(true)}
              className="hidden sm:flex"
              data-testid="bulk-import-btn"
            >
              <UploadSimple className="h-4 w-4 md:mr-1.5" />
              <span className="hidden md:inline">Bulk import</span>
            </Button>
            <Button onClick={() => setCreateOpen(true)} size="sm" data-testid="create-project-btn">
              <Plus className="h-4 w-4 md:mr-1.5" />
              <span className="hidden md:inline">New project</span>
            </Button>
          </>
        )}
      />

      {stats && (
        <AdminKpiStrip>
          <AdminKpi label="Total" value={stats.total} />
          <AdminKpi label="Drafts" value={stats.by_status?.draft?.count || 0} />
          <AdminKpi label="Funding" value={stats.by_status?.funding?.count || 0} />
          <AdminKpi label="Exited" value={stats.by_status?.exited?.count || 0} />
          <AdminKpi label="Target" value={`€${((stats.total_funding_target || 0) / 1000000).toFixed(1)}M`} />
          <AdminKpi label="Raised" value={`€${((stats.total_funding_current || 0) / 1000000).toFixed(1)}M`} />
        </AdminKpiStrip>
      )}


      <AdminToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search projects by title, location or code…"
        filters={[
          { key: 'status', label: 'Status', options: Object.entries(statusConfig).map(([value, c]) => ({ value, label: c.label })) },
          { key: 'project_type', label: 'Type', options: typeOptions },
          { key: 'funding', label: 'Funding', options: FUNDING_RANGES },
          { key: 'irr', label: 'IRR', options: IRR_RANGES },
          { key: 'hold', label: 'Target hold', options: HOLD_RANGES },
        ]}
        values={filters}
        onFilter={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
        onClear={() => setFilters({ status: 'all', project_type: 'all', funding: 'all', irr: 'all', hold: 'all' })}
        sortOptions={[
          { value: 'newest', label: 'Newest first' },
          { value: 'irr', label: 'Highest IRR' },
          { value: 'funding', label: 'Most funded' },
          { value: 'title', label: 'Title A-Z' },
        ]}
        sort={sort}
        onSort={setSort}
        view={viewMode}
        onView={handleViewModeChange}
        resultLabel={`${visibleProjects.length} of ${projects.length} projects`}
      />

        {/* Projects List */}
        {loading ? (
          <div className="flex items-center justify-center py-10 sm:py-16">
            <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : visibleProjects.length === 0 ? (
          <Card className="bg-white p-8 sm:p-12 text-center rounded-xl border border-black/[0.04]">
            <Buildings className="h-10 w-10 sm:h-12 sm:w-12 text-foreground-muted mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium mb-2">No projects found</h3>
            <p className="text-sm text-foreground-muted mb-4">
              {filterStatus !== 'all' ? 'Try adjusting your filters' : 'Create your first project'}
            </p>
            <Button onClick={() => setCreateOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Create Project
            </Button>
          </Card>
        ) : viewMode === 'card' ? (
          /* Card View - Single column on mobile */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {visibleProjects.map((project) => (
              <ProjectCard key={project.id} project={project} onClick={handleProjectClick} />
            ))}
          </div>
        ) : (
          /* List/Table View */
          <Card className="bg-white rounded-xl border border-black/[0.04] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-black/[0.08] bg-slate-50">
                    <th className="text-left py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Project</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Status</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Type</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Location</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Funding</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">IRR</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Hold</th>
                    <th className="text-right py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleProjects.map((project) => (
                    <ProjectListRow key={project.id} project={project} onClick={handleProjectClick} />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
      )}

      <ProjectFormDialog
        open={createOpen || !!editProject}
        onOpenChange={(open) => {
          if (!open) {
            setCreateOpen(false);
            setEditProject(null);
          }
        }}
        project={editProject}
        onSave={() => {
          fetchData();
          setEditProject(null);
        }}
      />

      <BulkImportDialog
        open={bulkImportOpen}
        onOpenChange={setBulkImportOpen}
        onSuccess={fetchData}
      />
    </div>
  );
}
